/**
 * Reine Hilfsfunktionen für die Zeitpläne der Prüfläufe: Auswahllisten, deutsche Texte und die
 * clientseitige Vorprüfung des Formulars.
 *
 * <p>Die Typen sind hier bewusst als String-Unions geschrieben und nicht aus dem generierten Client
 * importiert: die Funktionen sollen ohne generierten Code testbar bleiben. Die Werte entsprechen den
 * Backend-Enums (`SchedulePattern`, `ScheduleWeekday`, `ScheduleStatus`, `SchedulePauseReason`,
 * `ScheduleRunOutcome`). Maßgeblich für die Gültigkeit eines Zeitplans ist die Vorschau des
 * Backends (`POST …/preview`), nicht {@link validateScheduleForm}.
 */

import {parseGermanDate} from '../date/local-date';

export type SchedulePatternValue = 'ONCE' | 'DAILY' | 'WEEKLY' | 'CRON';
export type ScheduleWeekdayValue =
  'MONDAY' | 'TUESDAY' | 'WEDNESDAY' | 'THURSDAY' | 'FRIDAY' | 'SATURDAY' | 'SUNDAY';
export type ScheduleStatusValue = 'ACTIVE' | 'PAUSED' | 'FINISHED';
export type SchedulePauseReasonValue = 'MANUAL' | 'NO_CREDITS' | 'REPEATED_FAILURES' | 'NOT_EXECUTABLE';
export type ScheduleOutcomeValue =
  'CLAIMED' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'CANCELLED' | 'NOT_STARTED'
  | 'SKIPPED_OVERLAP' | 'SKIPPED_NO_FILES' | 'SKIPPED_NO_CREDITS' | 'SKIPPED_NOT_EXECUTABLE' | 'LOST';

/** Die Felder, aus denen sich die Wiederholung ergibt (Request wie View). */
export interface RecurrenceLike {
  pattern: SchedulePatternValue | string;
  weekdays?: (ScheduleWeekdayValue | string)[] | null;
  intervalWeeks?: number | null;
  /** `HH:mm`; bei CRON leer. */
  timeOfDay?: string | null;
  /** Nur bei CRON. */
  cronExpression?: string | null;
  startDate: string;
  endDate?: string | null;
}

/**
 * Zustand des Zeitplan-Formulars im Popup. Datum als `yyyy-MM-dd`. `timeOfDay` ist der Text aus dem
 * Uhrzeitfeld; gesendet wird er erst normalisiert ({@link normalizeTimeInput}).
 */
export interface ScheduleFormValue {
  definitionId: string | null;
  pattern: SchedulePatternValue;
  weekdays: ScheduleWeekdayValue[];
  intervalWeeks: number;
  timeOfDay: string;
  /** Nur bei CRON: 5 Felder „Minute Stunde Tag Monat Wochentag“. */
  cronExpression: string;
  startDate: string;
  endDate: string | null;
  notifyOnFailure: boolean;
  notifyOnSuccess: boolean;
  notifyUserIds: number[];
  /** Externe Adressen, kommagetrennt wie im Builder-Schritt „Bericht". */
  notifyEmailsText: string;
}

/** Body für Anlegen, Bearbeiten und Vorschau – strukturgleich zum generierten `CheckRunScheduleRequest`. */
export interface ScheduleRequestBody {
  definitionId?: string;
  pattern: SchedulePatternValue;
  weekdays?: ScheduleWeekdayValue[];
  intervalWeeks?: number;
  /** Pflicht außer bei CRON. */
  timeOfDay?: string;
  /** Nur bei CRON. */
  cronExpression?: string;
  startDate: string;
  endDate?: string;
  notifyOnFailure: boolean;
  notifyOnSuccess: boolean;
  notifyUserIds?: number[];
  notifyEmails?: string[];
}

export interface WeekdayOption {
  value: ScheduleWeekdayValue;
  short: string;
  long: string;
  /** Adverb für „Alle 2 Wochen dienstags". */
  adverb: string;
}

export const WEEKDAY_OPTIONS: readonly WeekdayOption[] = [
  {value: 'MONDAY', short: 'Mo', long: 'Montag', adverb: 'montags'},
  {value: 'TUESDAY', short: 'Di', long: 'Dienstag', adverb: 'dienstags'},
  {value: 'WEDNESDAY', short: 'Mi', long: 'Mittwoch', adverb: 'mittwochs'},
  {value: 'THURSDAY', short: 'Do', long: 'Donnerstag', adverb: 'donnerstags'},
  {value: 'FRIDAY', short: 'Fr', long: 'Freitag', adverb: 'freitags'},
  {value: 'SATURDAY', short: 'Sa', long: 'Samstag', adverb: 'samstags'},
  {value: 'SUNDAY', short: 'So', long: 'Sonntag', adverb: 'sonntags'},
];

/** Mindestabstand zweier Ausführungen im Expertenmodus; das Backend prüft ihn verbindlich. */
export const MIN_CRON_INTERVAL_MINUTES = 5;

/** Meldung bei falscher Feldanzahl – wortgleich mit dem Backend (`ScheduleRecurrence`). */
export const CRON_FIELD_COUNT_MESSAGE =
  'Der Cron-Ausdruck braucht genau 5 Felder: Minute Stunde Tag Monat Wochentag.';

export const INTERVAL_OPTIONS: readonly { value: number; label: string }[] =
  Array.from({length: 12}, (_, i) => ({
    value: i + 1,
    label: i === 0 ? 'Jede Woche' : `Alle ${i + 1} Wochen`,
  }));

export const PATTERN_OPTIONS: readonly { value: SchedulePatternValue; label: string }[] = [
  {value: 'ONCE', label: 'Einmalig'},
  {value: 'DAILY', label: 'Täglich'},
  {value: 'WEEKLY', label: 'Wöchentlich'},
];

export const MAX_RECIPIENTS = 20;

const WEEKDAY_INDEX = new Map<string, number>(WEEKDAY_OPTIONS.map((o, i) => [o.value, i]));
const SIMPLE_EMAIL = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/;
const BERLIN_FORMAT = new Intl.DateTimeFormat('de-DE', {
  timeZone: 'Europe/Berlin',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

function pad2(value: number): string {
  return value < 10 ? `0${value}` : `${value}`;
}

/**
 * Freitext-Uhrzeit → `HH:mm`, sonst `null`. Erlaubt sind `8`, `08`, `8:05`, `08.05`, `0805` und ein
 * angehängtes „Uhr“.
 */
export function normalizeTimeInput(text: string | null | undefined): string | null {
  const value = (text ?? '').trim().replace(/\s*uhr$/i, '');
  let match = /^(\d{1,2})(?:[:.](\d{1,2}))?$/.exec(value);
  if (!match) {
    match = /^(\d{2})(\d{2})$/.exec(value);
  }
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = match[2] === undefined ? 0 : Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  // „8:5“ ist mehrdeutig (8:05 oder 8:50) und wird abgelehnt; einstellige Minuten nur mit führender Null.
  if (match[2] !== undefined && match[2].length === 1) return null;
  return `${pad2(hours)}:${pad2(minutes)}`;
}

/** Cron-Ausdruck mit einfachen Leerzeichen; die Felder selbst prüft das Backend. */
export function normalizeCronInput(text: string | null | undefined): string {
  return (text ?? '').trim().split(/\s+/).filter(part => !!part).join(' ');
}

/** `2026-10-15` → `15.10.2026`; Unlesbares kommt unverändert zurück. */
export function formatDateDe(isoDate: string | null | undefined): string {
  if (!isoDate) return '';
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  return match ? `${match[3]}.${match[2]}.${match[1]}` : isoDate;
}

/** `15.10.2026` → `2026-10-15`, sonst `null` (auch bei Tagen wie dem 31.02.). */
export function parseDateDe(text: string | null | undefined): string | null {
  const date = parseGermanDate(text);
  return date ? toIsoDate(date) : null;
}

/**
 * Datum aus den <b>lokalen</b> Datumsteilen (`yyyy-MM-dd`). Nicht `toISOString`: das rechnet in
 * UTC um und verschiebt Mitternacht deutscher Zeit auf den Vortag.
 */
export function toIsoDate(date: Date): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

/**
 * `2026-10-15` → `Date` um 0:00 Uhr <b>lokaler</b> Zeit (Gegenstück zu {@link toIsoDate}); bei
 * allem anderen, auch bei Tagen wie dem 31.02., `null`.
 *
 * <p>Damit prüft das Zeitplan-Popup ein ISO-Datum und gibt es dem Datumsfeld als `Date`. Seit
 * bic-ui-components 1.0.57 formatiert `bic-input` mit `bicDatepickerInput` auch ISO- und deutsche
 * Strings als `TT.MM.JJJJ`.
 */
export function isoToLocalDate(isoDate: string | null | undefined): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate ?? '');
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return toIsoDate(date) === isoDate ? date : null;
}

/** Heutiges Datum in deutscher Zeit als `yyyy-MM-dd`. */
export function todayBerlinIso(now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Berlin', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(now);
  const part = (type: string) => parts.find(p => p.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}`;
}

/** ISO-Instant (UTC) → `15.10.2026, 12:00 Uhr` in deutscher Zeit; leer bei fehlendem Wert. */
export function formatBerlin(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return `${BERLIN_FORMAT.format(date)} Uhr`;
}

function sortedWeekdays(weekdays: readonly string[] | null | undefined): WeekdayOption[] {
  return [...new Set(weekdays ?? [])]
    .filter(day => WEEKDAY_INDEX.has(day))
    .sort((a, b) => WEEKDAY_INDEX.get(a)! - WEEKDAY_INDEX.get(b)!)
    .map(day => WEEKDAY_OPTIONS[WEEKDAY_INDEX.get(day)!]);
}

/** „Mo“, „Mo und Fr“, „Mo, Mi und Fr“. */
function joinGerman(items: string[]): string {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} und ${items[items.length - 1]}`;
}

/**
 * Lesbare Beschreibung der Wiederholung, z. B. „Täglich um 08:00 Uhr“ oder
 * „Alle 2 Wochen Mi und Fr um 12:00 Uhr bis 31.12.2026“.
 *
 * @param todayIso heutiges Datum (`yyyy-MM-dd`) für den Zusatz „ab …“ bei künftigem Start.
 */
export function describeRecurrence(spec: RecurrenceLike, todayIso: string = todayBerlinIso()): string {
  const time = `um ${spec.timeOfDay ?? ''} Uhr`;
  if (spec.pattern === 'ONCE') {
    return `Einmalig am ${formatDateDe(spec.startDate)} ${time}`;
  }

  let text: string;
  if (spec.pattern === 'CRON') {
    text = `Cron „${spec.cronExpression ?? ''}“`;
  } else if (spec.pattern === 'DAILY') {
    text = `Täglich ${time}`;
  } else {
    const days = sortedWeekdays(spec.weekdays);
    const interval = spec.intervalWeeks && spec.intervalWeeks > 1 ? spec.intervalWeeks : 1;
    if (interval === 1) {
      text = days.length === 1
        ? `Jeden ${days[0].long} ${time}`
        : `Jeden ${joinGerman(days.map(d => d.short))} ${time}`;
    } else {
      const dayText = days.length === 1 ? days[0].adverb : joinGerman(days.map(d => d.short));
      text = `Alle ${interval} Wochen ${dayText} ${time}`;
    }
  }

  if (spec.startDate && spec.startDate > todayIso) {
    text += ` ab ${formatDateDe(spec.startDate)}`;
  }
  if (spec.endDate) {
    text += ` bis ${formatDateDe(spec.endDate)}`;
  }
  return text;
}

export function pauseReasonLabel(reason: SchedulePauseReasonValue | string | null | undefined): string {
  switch (reason) {
    case 'MANUAL':
      return 'Manuell pausiert';
    case 'NO_CREDITS':
      return 'Kein Kontingent';
    case 'REPEATED_FAILURES':
      return '3 Fehlschläge in Folge';
    case 'NOT_EXECUTABLE':
      return 'Prüflauf nicht ausführbar';
    default:
      return 'Pausiert';
  }
}

export function outcomeLabel(outcome: ScheduleOutcomeValue | string | null | undefined): string {
  switch (outcome) {
    case 'COMPLETED':
      return 'Erfolgreich';
    case 'FAILED':
      return 'Fehlgeschlagen';
    case 'CANCELLED':
      return 'Abgebrochen';
    case 'NOT_STARTED':
      return 'Nicht gestartet';
    case 'SKIPPED_OVERLAP':
      return 'Übersprungen – vorheriger Lauf lief noch';
    case 'SKIPPED_NO_FILES':
      return 'Übersprungen – keine passenden Dateien';
    case 'SKIPPED_NO_CREDITS':
      return 'Übersprungen – kein Kontingent';
    case 'SKIPPED_NOT_EXECUTABLE':
      return 'Übersprungen – nicht ausführbar';
    case 'CLAIMED':
    case 'RUNNING':
      return 'Läuft';
    case 'LOST':
      return 'Unterbrochen';
    default:
      return '';
  }
}

export interface BadgeColors {
  bgClass: string;
  textClass: string;
}

export function outcomeBadge(outcome: ScheduleOutcomeValue | string | null | undefined): BadgeColors {
  switch (outcome) {
    case 'COMPLETED':
      return {bgClass: 'bg-green-100', textClass: 'text-green-900'};
    case 'FAILED':
    case 'NOT_STARTED':
    case 'LOST':
      return {bgClass: 'bg-red-100', textClass: 'text-red'};
    case 'SKIPPED_OVERLAP':
    case 'SKIPPED_NO_FILES':
    case 'SKIPPED_NO_CREDITS':
    case 'SKIPPED_NOT_EXECUTABLE':
      return {bgClass: 'bg-yellow-100', textClass: 'text-yellow-900'};
    default:
      return {bgClass: 'bg-gray-200', textClass: 'text-gray-700'};
  }
}

export function statusLabel(status: ScheduleStatusValue | string | null | undefined): string {
  switch (status) {
    case 'ACTIVE':
      return 'Aktiv';
    case 'PAUSED':
      return 'Pausiert';
    case 'FINISHED':
      return 'Beendet';
    default:
      return '';
  }
}

export function statusBadge(status: ScheduleStatusValue | string | null | undefined): BadgeColors {
  switch (status) {
    case 'ACTIVE':
      return {bgClass: 'bg-green-100', textClass: 'text-green-900'};
    case 'PAUSED':
      return {bgClass: 'bg-yellow-100', textClass: 'text-yellow-900'};
    default:
      return {bgClass: 'bg-gray-200', textClass: 'text-gray-700'};
  }
}

/** Kommagetrennte Adressen wie im Builder (`updateRecipients`), ohne leere Einträge und Dubletten. */
export function parseEmailList(text: string | null | undefined): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const raw of (text ?? '').split(/[,;]/)) {
    const email = raw.trim();
    const key = email.toLowerCase();
    if (!email || seen.has(key)) continue;
    seen.add(key);
    result.push(email);
  }
  return result;
}

/**
 * Clientseitige Vorprüfung des Formulars. Liefert deutsche Texte, leer = nichts auszusetzen.
 * Die Prüfung des Backends (Vorschau) bleibt maßgeblich, u. a. für die Eignung des Prüflaufs.
 */
export function validateScheduleForm(form: ScheduleFormValue): string[] {
  const issues: string[] = [];
  if (!form.definitionId) {
    issues.push('Bitte einen Prüflauf wählen.');
  }
  if (form.pattern === 'WEEKLY' && form.weekdays.length === 0) {
    issues.push('Bitte mindestens einen Wochentag wählen.');
  }
  if (form.pattern === 'WEEKLY' && !(form.intervalWeeks >= 1 && form.intervalWeeks <= 12)) {
    issues.push('Der Rhythmus muss zwischen 1 und 12 Wochen liegen.');
  }
  if (form.pattern === 'CRON') {
    const cron = normalizeCronInput(form.cronExpression);
    if (!cron) {
      issues.push('Bitte einen Cron-Ausdruck angeben.');
    } else if (cron.split(' ').length !== 5) {
      issues.push(CRON_FIELD_COUNT_MESSAGE);
    }
  } else if (!form.timeOfDay.trim()) {
    issues.push('Bitte eine Uhrzeit angeben.');
  } else if (normalizeTimeInput(form.timeOfDay) === null) {
    issues.push('Bitte die Uhrzeit im Format HH:MM angeben, z. B. 08:30.');
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(form.startDate)) {
    issues.push(form.pattern === 'ONCE' ? 'Bitte ein Datum wählen.' : 'Bitte ein Startdatum wählen.');
  } else if (form.pattern !== 'ONCE' && form.endDate && form.endDate < form.startDate) {
    issues.push('Das Enddatum darf nicht vor dem Startdatum liegen.');
  }

  const emails = parseEmailList(form.notifyEmailsText);
  const invalid = emails.filter(email => !SIMPLE_EMAIL.test(email));
  if (invalid.length > 0) {
    issues.push(`Ungültige E-Mail-Adresse: ${invalid.join(', ')}`);
  }
  const recipientCount = new Set(form.notifyUserIds).size + emails.length;
  if ((form.notifyOnFailure || form.notifyOnSuccess) && recipientCount === 0) {
    issues.push('Bitte mindestens einen Empfänger angeben.');
  }
  if (recipientCount > MAX_RECIPIENTS) {
    issues.push(`Höchstens ${MAX_RECIPIENTS} Empfänger möglich.`);
  }
  return issues;
}

/** Ergebnis von {@link retainMemberRecipients}. */
export interface RetainedRecipients {
  /** Empfänger, die Projektmitglieder sind, in der bisherigen Reihenfolge. */
  kept: number[];
  /** Anzahl der verschiedenen entfernten Nutzer-IDs. */
  removedCount: number;
}

/**
 * Kürzt die gewählten Empfänger auf die Projektmitglieder. Ein gespeicherter Zeitplan kann Nutzer
 * enthalten, die das Projekt inzwischen verlassen haben; das Backend lehnt solche IDs ab, und das
 * Select kann sie nicht anzeigen.
 *
 * <p>Nur mit einer <b>erfolgreich geladenen</b> Mitgliederliste aufrufen. Nach einem Ladefehler ist
 * die Liste leer, und die Funktion würde dann alle Empfänger entfernen.
 */
export function retainMemberRecipients(
  userIds: readonly number[],
  memberIds: readonly number[],
): RetainedRecipients {
  const members = new Set(memberIds);
  const kept = userIds.filter(id => members.has(id));
  const removed = new Set(userIds.filter(id => !members.has(id)));
  return {kept, removedCount: removed.size};
}

/** Hinweis im Popup, wenn {@link retainMemberRecipients} Empfänger entfernt hat; sonst `null`. */
export function removedRecipientsHint(removedCount: number): string | null {
  if (removedCount <= 0) return null;
  const subject = removedCount === 1
    ? '1 Empfänger ist kein Mitglied dieses Projekts und wurde'
    : `${removedCount} Empfänger sind keine Mitglieder dieses Projekts und wurden`;
  return `${subject} aus der Empfängerliste entfernt. Die Änderung wird beim Speichern übernommen.`;
}

/** Formular → Request-Body. Felder, die das Muster nicht braucht, werden weggelassen. */
export function toScheduleRequest(form: ScheduleFormValue): ScheduleRequestBody {
  const body: ScheduleRequestBody = {
    pattern: form.pattern,
    startDate: form.startDate,
    notifyOnFailure: form.notifyOnFailure,
    notifyOnSuccess: form.notifyOnSuccess,
    notifyUserIds: [...new Set(form.notifyUserIds)],
    notifyEmails: parseEmailList(form.notifyEmailsText),
  };
  if (form.definitionId) {
    body.definitionId = form.definitionId;
  }
  if (form.pattern === 'CRON') {
    body.cronExpression = normalizeCronInput(form.cronExpression);
  } else {
    body.timeOfDay = normalizeTimeInput(form.timeOfDay) ?? form.timeOfDay.trim();
  }
  if (form.pattern === 'WEEKLY') {
    body.weekdays = sortedWeekdays(form.weekdays).map(d => d.value);
    body.intervalWeeks = form.intervalWeeks;
  }
  if (form.pattern !== 'ONCE' && form.endDate) {
    body.endDate = form.endDate;
  }
  return body;
}

/** Reihenfolge der Übersicht: aktive nach nächster Ausführung, dann pausierte, dann beendete. */
export function sortSchedules<T extends { status: string; nextRunAt?: string | null; definitionName?: string | null }>(
  schedules: readonly T[],
): T[] {
  const rank = (status: string) => status === 'ACTIVE' ? 0 : status === 'PAUSED' ? 1 : 2;
  return [...schedules].sort((a, b) => {
    const byStatus = rank(a.status) - rank(b.status);
    if (byStatus !== 0) return byStatus;
    const aNext = a.nextRunAt ?? '';
    const bNext = b.nextRunAt ?? '';
    if (aNext !== bNext) {
      if (!aNext) return 1;
      if (!bNext) return -1;
      return aNext < bNext ? -1 : 1;
    }
    return (a.definitionName ?? '').localeCompare(b.definitionName ?? '', 'de');
  });
}
